/**
 * 会话服务
 * 会话列表管理和会话相关操作
 */
import { getTIM } from '../sdk/tim/index.js'
import { eventBus } from '../sdk/eventBus.js'
import TencentCloudChat from '@tencentcloud/chat'

/**
 * 初始化会话事件监听
 */
export function initConversationListener() {
  const tim = getTIM()
  if (!tim) {
    console.warn('[ConversationService] TIM not initialized')
    return
  }

  const Event = TencentCloudChat.EVENT

  // 会话列表更新
  tim.on(Event.CONVERSATION_LIST_UPDATED, (event) => {
    console.log('[ConversationService] CONVERSATION_LIST_UPDATED')
    eventBus.emit('CONVERSATION_LIST_UPDATED', event.data || [])
  })
}

/**
 * 获取会话列表（从 TIM SDK）
 * @returns {Promise<Array>}
 */
export async function getTIMConversationList() {
  const tim = getTIM()
  if (!tim) return []

  try {
    const res = await tim.getConversationList()
    return res.data.conversationList || []
  } catch (error) {
    console.error('[ConversationService] getConversationList failed:', error)
    return []
  }
}

/**
 * 合并 TIM SDK 和后端会话列表
 * 策略：以 TIM SDK 会话为主，后端会话补充（用于补充未在 TIM 中同步的会话）
 * 后端会话会通过 tim.getConversationProfile 补全用户资料
 * @param {Array} timList - TIM SDK 会话列表
 * @param {Array} backendList - 后端会话列表
 * @returns {Promise<Array>} 合并后的会话列表
 */
export async function mergeConversationLists(timList, backendList) {
  const merged = new Map()

  // 1. 先添加 TIM SDK 会话（优先级高）
  timList.forEach(conv => {
    merged.set(conv.conversationID, { ...conv, _source: 'tim' })
  })

  // 2. 收集需要补全的后端会话（TIM 列表中不存在的）
  const needEnrich = []
  backendList.forEach(item => {
    const convId = `C2C${item.from_account}`
    const existing = merged.get(convId)

    if (existing) {
      // 已存在：以 TIM 为主，但合并未读数（取较大值）
      existing.unreadCount = Math.max(existing.unreadCount || 0, item.unread_count || 0)
      existing._source = 'merged'
    } else {
      // 不存在：先加入后端原始数据，稍后尝试用 TIM 补全
      needEnrich.push({ convId, item })
    }
  })

  // 3. 对后端会话尝试通过 TIM SDK 补全资料
  const tim = getTIM()
  if (tim && needEnrich.length > 0) {
    const enrichResults = await Promise.allSettled(
      needEnrich.map(({ convId }) =>
        tim.getConversationProfile(convId)
          .then(res => ({ convId, profile: res.data.conversation }))
          .catch(() => ({ convId, profile: null }))
      )
    )

    enrichResults.forEach((result, index) => {
      const { convId, item } = needEnrich[index]
      if (result.status === 'fulfilled' && result.value.profile) {
        // TIM 有此会话的完整资料，使用 TIM 数据（优先）
        const profile = result.value.profile
        profile.unreadCount = Math.max(profile.unreadCount || 0, item.unread_count || 0)
        profile._source = 'enriched'
        merged.set(convId, profile)
      } else {
        // TIM 也无此会话，使用后端原始数据
        merged.set(convId, {
          conversationID: convId,
          type: 'C2C',
          unreadCount: item.unread_count || 0,
          lastMessage: {
            lastTime: item.last_time || 0,
            type: 'TIMTextElem',
            payload: { text: item.last_msg || '' },
            isRevoked: false
          },
          userProfile: {
            userID: item.from_account,
            nick: item.nick || item.from_account,
            avatar: item.avatar || ''
          },
          _source: 'backend'
        })
      }
    })
  } else {
    // TIM 未就绪，直接使用后端原始数据
    needEnrich.forEach(({ convId, item }) => {
      merged.set(convId, {
        conversationID: convId,
        type: 'C2C',
        unreadCount: item.unread_count || 0,
        lastMessage: {
          lastTime: item.last_time || 0,
          type: 'TIMTextElem',
          payload: { text: item.last_msg || '' },
          isRevoked: false
        },
        userProfile: {
          userID: item.from_account,
          nick: item.nick || item.from_account,
          avatar: item.avatar || ''
        },
        _source: 'backend'
      })
    })
  }

  // 4. 按 lastMessage.lastTime 排序
  const result = [...merged.values()]
  result.sort((a, b) => (b.lastMessage?.lastTime || 0) - (a.lastMessage?.lastTime || 0))

  return result
}

/**
 * 删除会话
 * @param {string} conversationID
 */
export async function deleteConversation(conversationID) {
  const tim = getTIM()
  if (!tim) throw new Error('TIM not initialized')

  return tim.deleteConversation(conversationID)
}

/**
 * 设置会话置顶
 * @param {string} conversationID
 * @param {boolean} isPinned
 */
export async function pinConversation(conversationID, isPinned) {
  const tim = getTIM()
  if (!tim) throw new Error('TIM not initialized')

  return tim.pinConversation(conversationID, isPinned)
}

/**
 * 将会话静音
 * @param {string} conversationID
 * @param {boolean} isMuted
 */
export async function muteConversation(conversationID, isMuted) {
  const tim = getTIM()
  if (!tim) throw new Error('TIM not initialized')

  return tim.muteConversation(conversationID, isMuted)
}

/**
 * 获取会话未读数
 * @param {string} conversationID
 */
export function getConversationUnreadCount(conversationID) {
  const tim = getTIM()
  if (!tim) return 0

  try {
    const conv = tim.getConversationInfo(conversationID)
    return conv?.unreadCount || 0
  } catch {
    return 0
  }
}

export default {
  initConversationListener,
  getTIMConversationList,
  mergeConversationLists,
  deleteConversation,
  pinConversation,
  muteConversation,
  getConversationUnreadCount
}
